import { useRef, useState } from "react";
import { afterFirstLcp } from "@wags/tanstack-start/after-first-lcp.ts";
import { MOBILE_VIEWPORT_QUERY } from "@wags/tanstack-start/react";
import { isConstrainedNetwork } from "@/lib/core/constrained-network";
import { playOrUseAnimatedImage } from "./find-hero-video";

export const FIND_HERO_POSTER_SRC = "/hero/find-hero-poster.jpg";
const VIDEO_DESKTOP = "/hero/find-hero.mp4";
const VIDEO_MOBILE = "/hero/find-hero-mobile.mp4";

function prefersPosterOnly(): boolean {
    return (
        window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
        isConstrainedNetwork()
    );
}

export function FindHeroBackground({ paused = false }: { paused?: boolean }) {
    const [animatedImageSrc, setAnimatedImageSrc] = useState<string | null>(
        null,
    );
    const posterOnly = useRef(false);
    const lifecycleAttempt = useRef(0);

    const ownVideo = (video: HTMLVideoElement | null) => {
        if (!video || posterOnly.current) return;
        const attempt = lifecycleAttempt.current + 1;
        lifecycleAttempt.current = attempt;
        const abort = new AbortController();
        const dispose = () => {
            lifecycleAttempt.current += 1;
            abort.abort();
            video.pause();
        };

        const play = () => {
            const source = video.getAttribute("src");
            if (!source) return;

            void playOrUseAnimatedImage(video).then((useAnimatedImage) => {
                if (useAnimatedImage && lifecycleAttempt.current === attempt) {
                    setAnimatedImageSrc(source);
                }
            });
        };

        if (video.src) {
            if (paused) video.pause();
            else play();
            return dispose;
        }

        if (prefersPosterOnly()) {
            posterOnly.current = true;
            return dispose;
        }

        const attachSource = () => {
            if (lifecycleAttempt.current !== attempt) return;
            video.src = window.matchMedia(MOBILE_VIEWPORT_QUERY).matches
                ? VIDEO_MOBILE
                : VIDEO_DESKTOP;
            if (!paused) play();
        };

        void afterFirstLcp(abort.signal).then(attachSource);

        return dispose;
    };

    return (
        <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 overflow-hidden bg-doberman-black"
        >
            {animatedImageSrc && !paused ? (
                <img
                    src={animatedImageSrc}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover"
                    onError={() => {
                        posterOnly.current = true;
                        setAnimatedImageSrc(null);
                    }}
                />
            ) : (
                <>
                    <img
                        src={FIND_HERO_POSTER_SRC}
                        alt=""
                        fetchPriority="high"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                    <video
                        ref={ownVideo}
                        className="absolute inset-0 h-full w-full object-cover"
                        loop
                        muted
                        playsInline
                        preload="none"
                    />
                </>
            )}
            <div className="absolute inset-0 bg-doberman-black/80" />
        </div>
    );
}
